import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/kfmApi";
import StrongsAccordion from "@/components/StrongsAccordion";
import Highlight from "@/components/Highlight";
import { Input } from "@/components/ui/input";
import { Search, Languages, Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";

// Right-panel concordance (desktop): Strong's word studies + direct search.
// Collapsed by default with the companion present; expanded in Study Alone mode.
export default function ConcordancePanel({ session, defaultOpen = false }) {
  const study = session.study || {};
  const [open, setOpen] = useState(defaultOpen);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [mode, setMode] = useState("keyword");
  const [lastQuery, setLastQuery] = useState("");
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);

  const runSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setOpen(true);
    setSearching(true);
    try {
      const { data } = await api.get("/bible/search", {
        params: { query: query.trim(), translation: session.passage?.translation || "niv" },
      });
      setResults(data.results || []);
      setMode(data.mode || "keyword");
      setLastQuery(query.trim());
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setSearching(false);
  };

  return (
    <section data-testid="concordance-panel" className="px-6 py-5 space-y-4">
      <button
        data-testid="concordance-toggle"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-xs font-medium uppercase tracking-widest text-slate-500 hover:text-[#00E5FF] transition-colors"
      >
        <span className="flex items-center gap-2">
          <Languages className="w-4 h-4" strokeWidth={1.5} /> Concordance
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
      </button>

      {open && (
        <div className="space-y-4 animate-in fade-in duration-300">
          <form onSubmit={runSearch} className="flex gap-2">
            <Input
              data-testid="concordance-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="A word (grace) or reference (John 3:16)"
              className="h-10 rounded-xl border-[#29344D] text-sm"
            />
            <button
              data-testid="concordance-search-submit"
              type="submit"
              disabled={searching}
              className="h-10 rounded-xl bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] px-3 shrink-0 transition-colors"
            >
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </button>
          </form>

          {results && (
            <div data-testid="concordance-results" className="space-y-3 max-h-60 overflow-y-auto">
              {results.length === 0 && <p className="text-sm text-slate-500">No matches found.</p>}
              {results.map((r, i) => (
                <div key={i} className="border-b border-[#1E273D] pb-2 last:border-0">
                  <div className="text-xs text-[#00E5FF] mb-1">{r.reference}</div>
                  <p className="text-sm text-slate-300 leading-relaxed">
                    <Highlight text={r.text} query={mode === "keyword" ? lastQuery : ""} />
                  </p>
                </div>
              ))}
            </div>
          )}

          <StrongsAccordion entries={study.strongs} sessionId={session.id} reference={study.passage_reference} />
        </div>
      )}
    </section>
  );
}
